import * as React from 'react'
import { Slot } from 'primitives/slot'
import { useActivateTrialRootContext } from './activate-trial-root'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { tw } from 'utils/classnames'

interface ActivateTrialTriggerProps {
  asChild?: boolean
  children?: React.ReactNode
  className?: string
  confirmationLabel?: React.ReactNode
  onActivate?: () => void
  style?: React.CSSProperties
  testId?: string
}

export function ActivateTrialTrigger({
  asChild = false,
  children,
  className,
  confirmationLabel,
  onActivate,
  style,
  testId,
}: ActivateTrialTriggerProps) {
  const { getLoggedOutHref, handleLoggedInTrigger, isPending, isUserLoggedIn } =
    useActivateTrialRootContext()
  const [isConfirming, setIsConfirming] = React.useState(false)

  const content = isConfirming && confirmationLabel ? confirmationLabel : children

  const handleTriggerClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isPending) {
        event.preventDefault()
        return
      }

      if (confirmationLabel && !isConfirming) {
        event.preventDefault()
        setIsConfirming(true)
        return
      }

      handleLoggedInTrigger()
      onActivate?.()
    },
    [confirmationLabel, handleLoggedInTrigger, isConfirming, isPending, onActivate],
  )

  if (isUserLoggedIn) {
    if (asChild) {
      const slottedChild =
        isConfirming &&
        confirmationLabel &&
        React.isValidElement<{ children?: React.ReactNode }>(children)
          ? React.cloneElement(children, { children: confirmationLabel })
          : children

      return (
        <span className={tw('relative inline-flex', className)} style={style} data-testid={testId}>
          <Slot
            aria-disabled={isPending || undefined}
            className={isPending ? 'invisible' : undefined}
            onClick={handleTriggerClick}
          >
            {slottedChild}
          </Slot>
          {isPending ? (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <AnimatedSpinnerIcon />
            </span>
          ) : null}
        </span>
      )
    }

    return (
      <button
        type="button"
        disabled={isPending}
        onClick={handleTriggerClick}
        data-testid={testId}
        style={style}
        className={tw('relative', className)}
      >
        {isPending ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <AnimatedSpinnerIcon />
          </span>
        ) : null}
        <span className={isPending ? 'invisible' : 'visible'}>{content}</span>
      </button>
    )
  }

  if (asChild) {
    const loggedOutSlotProps = {
      className,
      style,
      'data-testid': testId,
      href: getLoggedOutHref(),
    } as React.ComponentPropsWithoutRef<typeof Slot>

    return <Slot {...loggedOutSlotProps}>{children}</Slot>
  }

  return (
    <a data-testid={testId} href={getLoggedOutHref()} style={style} className={className}>
      {children}
    </a>
  )
}
